import { Icons, Images } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import {
  SuspendProductService,
  UnSuspendProductService,
} from "shared/services/products";
import {
  DeleteProductReportService,
  GetProductReportsService,
} from "shared/services/reports/product";
import { handleErrors } from "shared/utils/helper";
import styles from "./style.module.scss";
import ReportedProductDetailsModal from "shared/modal/reportedProductDetail";

const ProductListings = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<ReportedProductDetail[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ReportedProductDetail | null>(null);
  const [suspendLoading, setSuspendLoading] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleShowDetailModal = () => {
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Reason", key: "reason" },
    { title: "Description", key: "desc" },
    { title: "Product", key: "Price" },
    { title: "Reported By", key: "status" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetProductReportsService(page, 10);
      setProducts(response.data.data.data || []);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      toastMessage("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedProduct) return;
    setSuspendLoading(true);
    if (selectedProduct?.product?.is_suspended) {
      UnSuspendProductService(selectedProduct.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "Product Activated Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    } else {
      SuspendProductService(selectedProduct.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "Product Suspended Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedProduct) return;
    setSuspendLoading(true);
    DeleteProductReportService(selectedProduct.id)
      .then(({ data, status }) => {
        if (status) {
          toastMessage("success", "Report Deleted Successfully");
          handleGetAllUsers(currentPage);
          handleCloseConfirmationModal();
        }
      })
      .catch((error) => {
        handleErrors(error);
      })
      .finally(() => {
        setSuspendLoading(false);
      });
  };

  useEffect(() => {
    handleGetAllUsers(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Reported Products"
          isEmpty={!loading && !products.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : products.map((data, idx) => (
                <tr key={data.id}>
                  <td style={{ maxWidth: "20%" }}>{data.reason}</td>
                  <td style={{ maxWidth: "200px" }}>
                    <label className={classNames(styles.elipsis)}>
                      {data.desc}
                    </label>
                  </td>
                  <td style={{ maxWidth: "20%" }}>
                    <div className="flex items-center gap-1">
                      <img
                        src={data?.product?.media?.[0]?.fullpath}
                        alt={"product photo"}
                        width="100"
                      />
                      <label>{data?.product?.title}</label>
                    </div>
                  </td>
                  <td style={{ maxWidth: "20%" }}>
                    <div className="flex items-center gap-1">
                      <img
                        src={
                          data?.reported_by?.profile_image?.fullpath
                            ? data?.reported_by?.profile_image?.fullpath
                            : Images.DefaultAvatar
                        }
                        alt={"user photo"}
                        width="100"
                      />
                      <label>{data?.reported_by?.name}</label>
                    </div>
                  </td>
                  <td style={{ maxWidth: "20%" }}>
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <button
                          onClick={() => {
                            setSelectedProduct(data);
                            handleShowDetailModal();
                          }}
                        >
                          <Icons.Eye />
                        </button>
                      </CustomToolTip>
                      {data.product?.is_suspended ? null : (
                        <CustomToolTip label="Suspend Product">
                          <button
                            onClick={() => {
                              setSelectedProduct(data);
                              handleShowConfirmationModal();
                            }}
                          >
                            <Icons.Reject />
                          </button>
                        </CustomToolTip>
                      )}
                      <CustomToolTip label="Delete Report">
                        <button
                          onClick={() => {
                            setIsDelete(true);
                            setSelectedProduct(data);
                            handleShowConfirmationModal();
                          }}
                        >
                          <Icons.TrashOutline />
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        handleSubmit={() => {
          if (isDelete) {
            handleDeleteUser();
          } else {
            handleSuspendUser();
          }
        }}
        loading={suspendLoading}
      />

      <ReportedProductDetailsModal
        show={showDetailModal}
        handleClose={handleCloseDetailModal}
        detail={selectedProduct}
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default ProductListings;
